import React from 'react';
import { motion, useInView } from 'framer-motion';
import { usePrefersReducedMotion } from '../../hooks/useMedia';
type RevealProps = {
  children: React.ReactNode;
  delay?: number;
  y?: number;
  className?: string;
  as?: 'div' | 'span' | 'li';
};

/** Fades + lifts content into view once, respecting reduced-motion. */
export function Reveal({
  children,
  delay = 0,
  y = 26,
  className,
  as = 'div'
}: RevealProps) {
  const reduced = usePrefersReducedMotion();
  const MotionTag = as === 'span' ? motion.span : as === 'li' ? motion.li : motion.div;
  return <MotionTag className={className} initial={reduced ? {
    opacity: 1
  } : {
    opacity: 0,
    y
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true,
    margin: '-10% 0px -10% 0px'
  }} transition={{
    duration: 0.9,
    delay,
    ease: [0.16, 1, 0.3, 1]
  }} data-id="element-586" data-mp="1npfbpchyjof">
      {children}
    </MotionTag>;
}
type MaskTextProps = {
  text: string;
  className?: string;
  lineClassName?: string;
  delay?: number;
  /** Split by word instead of by line for tighter type reveals. */
  by?: 'line' | 'word';
};

/**
 * Editorial mask reveal: lines (split on "|") or words rise out of a clipped box.
 */
export function MaskText({
  text,
  className,
  lineClassName,
  delay = 0,
  by = 'line'
}: MaskTextProps) {
  const reduced = usePrefersReducedMotion();
  const ref = React.useRef<HTMLSpanElement>(null);
  const inView = useInView(ref, {
    once: true,
    margin: '-8% 0px'
  });
  const parts = by === 'line' ? text.split('|') : text.split(' ');
  return <span ref={ref} className={className} data-id="element-587" data-mp="plvrip92yk">
      {parts.map((part, index) => <span key={`${part}-${index}`} className={by === 'line' ? 'block overflow-hidden pb-[0.08em]' : 'inline-block overflow-hidden pb-[0.08em] align-bottom'} data-id="element-588" data-mp="50u1lfbs9tnq" data-mpi={index}>
          <motion.span className={`block ${lineClassName ?? ''}`} initial={reduced ? {
        y: '0%'
      } : {
        y: '110%'
      }} animate={inView || reduced ? {
        y: '0%'
      } : {
        y: '110%'
      }} transition={{
        duration: 1,
        delay: delay + index * (by === 'line' ? 0.09 : 0.045),
        ease: [0.16, 1, 0.3, 1]
      }} data-id="element-589" data-mp="1gf7lya7bvqz0">
            {part}
            {by === 'word' && index < parts.length - 1 ? '\u00A0' : null}
          </motion.span>
        </span>)}
    </span>;
}

/** Section eyebrow with a hairline rule. */
export function SectionLabel({
  children,
  className
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return <div className={`flex items-center gap-3 text-muted ${className ?? ''}`} data-id="element-590" data-mp="2tu8pglu3828f">
      <span className="h-px w-8 bg-brand" aria-hidden="true" data-id="element-591" data-mp="37kkj4p6bm91o" />
      <span className="label" data-id="element-592" data-mp="32skggoyv921e">{children}</span>
    </div>;
}